import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { usePrices } from './usePrices';
import { useWalletSync } from './useWalletSync';
import { useBlockchains } from '../contexts/BlockchainProvider';
import { useCoins } from '../contexts/CoinsContext';
import api from '../services/api';
import { SUPPORTED_BLOCKCHAINS } from '../utils/blockchainUtils';
import { TRANSACTIONS_ENABLED } from '../config/featureFlags';
import {
  Typography,
  Box,
  Link,
  Chip
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export const useWalletsPage = () => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const { currency, setCurrency } = useCurrency();
  const { coins, loading: coinsLoading, refreshCoins } = useCoins();
  
  // Wallet sync functionality
  const {
    loading: syncLoading,
    error: syncError,
    groupedBalances,
    transactions,
    syncProgress,
    transactionPagination,
    syncWallet,
    addWallet,
    refreshGroupedBalances,
    fetchTransactions: fetchTransactionsRaw,
    getWallets,
    deleteWallet,
    clearError,
    clearSyncProgress,
    clearGroupedBalances,
    setTransactions,
  } = useWalletSync();

  // fetchTransactions is now gated by TRANSACTIONS_ENABLED. In simplified
  // mode the wallet-transactions UI is hidden, so there is no reason to
  // call /api/wallets/transactions. Manual holdings use /api/transactions
  // via fetchManualHoldings, which is NOT gated.
  const fetchTransactions = useCallback(
    async (...args) => {
      if (!TRANSACTIONS_ENABLED) {
        return { transactions: [], totalCount: 0, totalPages: 0 };
      }
      return fetchTransactionsRaw(...args);
    },
    [fetchTransactionsRaw]
  );

  // Manual holdings state
  const [manualHoldings, setManualHoldings] = useState([]);
  const [manualHoldingsLoading, setManualHoldingsLoading] = useState(false);

  // Tab state for different views
  const [activeTab, setActiveTab] = useState(1); // Default to wallets tab
  const [userManuallyChangedTab, setUserManuallyChangedTab] = useState(false);
  const [transactionsPerPage] = useState(20);

  const [wallets, setWallets] = useState([]);
  const [open, setOpen] = useState(false);
  const [modalType, setModalType] = useState('wallet'); // 'wallet' or 'transaction'
  const [newWallet, setNewWallet] = useState({
    blockchain: '',
    address: '',
    label: ''
  });
  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().slice(0, 10),
    type: 'Buy',
    coin: '',
    blockchain: '',
    amount: '',
    price: '',
    fee: '0'
  });
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [manualHoldingsSearchTerm, setManualHoldingsSearchTerm] = useState('');
  const [manualHoldingsSortColumn, setManualHoldingsSortColumn] = useState(() => localStorage.getItem('manualHoldingsSortColumn') || 'date');
  const [manualHoldingsSortDirection, setManualHoldingsSortDirection] = useState(() => localStorage.getItem('manualHoldingsSortDirection') || 'desc');
  const [manualHoldingsDeleteConfirmOpen, setManualHoldingsDeleteConfirmOpen] = useState(false);
  const [manualHoldingsToDelete, setManualHoldingsToDelete] = useState(null);
  
  // Manual holdings columns configuration
  const manualHoldingsAllColumns = useMemo(() => [
    { id: 'date', label: t('tables.transactions.date'), align: 'left' },
    { id: 'type', label: t('tables.transactions.type'), align: 'left' },
    { id: 'coin', label: t('tables.transactions.coin'), align: 'left' },
    { id: 'coinName', label: t('tables.transactions.coinName'), align: 'left' },
    { id: 'blockchain', label: 'Blockchain', align: 'left' },
    { id: 'amount', label: t('tables.transactions.amount'), align: 'right' },
    { id: 'price', label: 'Price (Buy/Current)', align: 'right' },
    { id: 'fee', label: t('tables.transactions.fee'), align: 'right' },
    { id: 'total', label: 'Total (Buy/Current)', align: 'right' },
  ], [t]);
  
  const manualHoldingsDefaultVisibleColumns = useMemo(() => 
    manualHoldingsAllColumns.map(col => col.id), 
    [manualHoldingsAllColumns]
  );
  const [manualHoldingsVisibleColumns, setManualHoldingsVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('manualHoldingsVisibleColumns');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure fee column is included if it's missing
      if (!parsed.includes('fee')) {
        return [...parsed, 'fee'];
      }
      return parsed;
    }
    // Default to showing all columns including the new gain columns
    return manualHoldingsDefaultVisibleColumns || [
      'date', 'type', 'coin', 'amount', 'price', 'fee', 'total'
    ];
  });
  
  const [manualHoldingsColumnsDialogOpen, setManualHoldingsColumnsDialogOpen] = useState(false);
  const [manualHoldingsTempVisibleColumns, setManualHoldingsTempVisibleColumns] = useState([]);
  const [manualHoldingsTempHiddenColumns, setManualHoldingsTempHiddenColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const { currentUser } = useAuth();
  const [walletTransactions, setWalletTransactions] = useState({});
  const [loadingTransactions, setLoadingTransactions] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [walletToDelete, setWalletToDelete] = useState(null);
  const [transactionFilters, setTransactionFilters] = useState({ coin: '', walletId: '' });
  const [transactionSort, setTransactionSort] = useState({ sortBy: 'date', sortOrder: 'desc' });
  const [sortColumn, setSortColumn] = useState('total_market_value');
  const [sortDirection, setSortDirection] = useState('desc');
  const [visibleColumns, setVisibleColumns] = useState([
    'label', 'blockchain', 'total_market_value', 'address'
  ]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [tempVisibleColumns, setTempVisibleColumns] = useState(visibleColumns);
  const [isRTL, setIsRTL] = useState(i18n.dir() === 'rtl');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [realOnChainBalance, setRealOnChainBalance] = useState(null);
  const [missingAmount, setMissingAmount] = useState(null);
  const [uniqueCoins, setUniqueCoins] = useState([]);
  const { blockchains, loading: blockchainsLoading } = useBlockchains();
  const [portfolioGains, setPortfolioGains] = useState({});
  
  // Debug log when portfolioGains state changes
  useEffect(() => {
    // console.log('[PortfolioGains] State changed:', portfolioGains);
  }, [portfolioGains]);
  const [loadingGains, setLoadingGains] = useState(false);

  const allCoinIdentifiers = React.useMemo(() => {
    if ((!groupedBalances || !Array.isArray(groupedBalances)) && (!transactions || !Array.isArray(transactions))) {
      return [];
    }
    // Only include coin symbols, not coingecko_id values
    // The priceService.fetchPrices method will handle mapping symbols to coingecko_id internally
    const balanceIdentifiers = (groupedBalances || []).map(b => b.coin).filter(Boolean);
    const transactionIdentifiers = (transactions || []).map(t => t.coin).filter(Boolean);
    const result = [...new Set([...balanceIdentifiers, ...transactionIdentifiers])].sort();
    
    // console.log(`[useWalletsPage] allCoinIdentifiers calculated:`, {
    //   groupedBalancesLength: groupedBalances?.length || 0,
    //   transactionsLength: transactions?.length || 0,
    //   balanceIdentifiers,
    //   transactionIdentifiers,
    //   finalResult: result
    // });
    
    return result;
  }, [groupedBalances, transactions]);

  const { prices, loading: loadingPrices, error: pricesError, refresh: refreshPrices, refreshWithIcons, forceRefresh: forceRefreshPrices, lastUpdate, freshnessInfo, isRefreshing } = usePrices(allCoinIdentifiers, true, 'WalletsPage');

  // Get all unique coins from transactions
  const allTransactionCoins = React.useMemo(() => {
    if (!transactions || !Array.isArray(transactions)) return [];
    if (transactionFilters.walletId) {
      // Only show coins for the selected wallet
      const coins = [
        ...new Set(
          transactions
            .filter(tx => String(tx.wallet_id) === String(transactionFilters.walletId))
            .map(tx => tx.coin)
            .filter(Boolean)
        ),
      ].sort();
      return coins;
    } else {
      // "All Wallets" selected: show all unique coins present in all transactions
      const coins = [
        ...new Set(transactions.map(tx => tx.coin).filter(Boolean))
      ].sort();
      return coins;
    }
  }, [transactions, transactionFilters.walletId]);

  useEffect(() => {
    // Wallet filter changed - no need to log this
  }, [transactionFilters.walletId, transactions]);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Set initial tab based on portfolio content after data is loaded
  useEffect(() => {
    const hasPortfolioContent = (groupedBalances && groupedBalances.length > 0) || 
                               (manualHoldings && manualHoldings.length > 0);
    
    // Only set initial tab if user hasn't manually changed it and we have data
    if (!userManuallyChangedTab && (groupedBalances !== undefined || manualHoldings !== undefined)) {
      setActiveTab(hasPortfolioContent ? 0 : 1);
    }
  }, [groupedBalances, manualHoldings, userManuallyChangedTab]);

  useEffect(() => {
    if (activeTab === 3) { // Manual Holdings tab
      fetchManualHoldings();
    }
  }, [activeTab, currentUser]);

  // Update active tab when portfolio content changes
  useEffect(() => {
    const hasPortfolioContent = (groupedBalances && groupedBalances.length > 0) || 
                               (manualHoldings && manualHoldings.length > 0);
    
    // Only auto-switch if user hasn't manually changed the tab
    if (!userManuallyChangedTab) {
      // If we're currently on wallets tab (1) but portfolio has content, switch to portfolio tab (0)
      if (activeTab === 1 && hasPortfolioContent) {
        setActiveTab(0);
      }
      // If we're currently on portfolio tab (0) but portfolio is empty, switch to wallets tab (1)
      else if (activeTab === 0 && !hasPortfolioContent) {
        setActiveTab(1);
      }
    }
  }, [groupedBalances, manualHoldings, activeTab, userManuallyChangedTab]);

  useEffect(() => {
    // Refetch transactions when filters or sorting change
    const combinedFilters = { ...transactionFilters, ...transactionSort };
    fetchTransactions(transactionsPerPage, 0, combinedFilters);
  }, [transactionFilters, transactionSort]);
  
  const loadInitialData = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setError('');
      
      // Load critical data in parallel
      const [walletsResult] = await Promise.all([
        getWallets(),
        refreshGroupedBalances(),
        fetchTransactions(),
        fetchManualHoldings()
      ]);
      setWallets(walletsResult || []); // <-- Ensure wallets state is set
      
      // Do NOT call fetchPortfolioGains here anymore
      
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Mark that user manually changed the tab
    setUserManuallyChangedTab(true);
  };

  // Handle transactions page change
  const handleTransactionsPageChange = (page) => {
    const combinedFilters = { ...transactionFilters, ...transactionSort };
    fetchTransactions(transactionsPerPage, (page - 1) * transactionsPerPage, combinedFilters);
  };

  const handleFilterChange = (filterName, value) => {
    setTransactionFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleTransactionSort = (newSortBy) => {
    setTransactionSort(prev => ({
      sortBy: newSortBy,
      sortOrder: prev.sortBy === newSortBy && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Enhanced wallet sync
  const handleSyncWallet = async (walletId) => {
    try {
      await syncWallet(walletId);
    } catch (err) {
      console.error('Error refreshing wallet:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to refresh wallet', severity: 'error' });
    }
  };

  // Enhanced wallet deletion
  const handleDeleteWallet = async (walletId) => {
    try {
      const result = await deleteWallet(walletId);
      
      // Update local state to remove the deleted wallet immediately
      setWallets(prevWallets => prevWallets.filter(wallet => wallet.id !== walletId));
      
      // Clear all cached data and reload fresh data
      clearGroupedBalances();
      setTransactions([]);
      
      // Reload all data from server to ensure consistency
      const [freshWallets, freshBalances, freshTransactions] = await Promise.all([
        getWallets(),
        refreshGroupedBalances(),
        fetchTransactions(transactionsPerPage, 0, { ...transactionFilters, ...transactionSort })
      ]);
      
      // Update state with fresh data
      setWallets(freshWallets || []);
      
      // Trigger price refresh after wallet delete
      if (typeof refreshPrices === 'function') {
        refreshPrices();
      }
      // Show success message with deletion details
      const message = result.deletedBalances || result.deletedTransactions 
        ? `Wallet deleted successfully! Removed ${result.deletedBalances || 0} balances and ${result.deletedTransactions || 0} transactions.`
        : 'Wallet deleted successfully!';
      
      setSnackbar({ open: true, message, severity: 'success' });
      setDeleteConfirmOpen(false);
      setWalletToDelete(null);
    } catch (error) {
      setSnackbar({ open: true, message: `Delete failed: ${error.message}`, severity: 'error' });
    }
  };

  // Manual refresh function to clear cache and reload data
  const handleManualRefresh = async () => {
    try {
      setLoading(true);
      // Clear all cached data
      clearGroupedBalances();
      setWallets([]);
      setTransactions([]);
      // Reload all data from server with fresh sync
      await api.get('/api/wallets/aggregated?fresh=true');
      await loadInitialData();
      setSnackbar({ open: true, message: 'Data refreshed successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error refreshing data:', error);
      setSnackbar({ open: true, message: `Refresh failed: ${error.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === 0) return '0';
    if (typeof value !== 'number' || isNaN(value)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(0);
    }
    const absValue = Math.abs(value);
    if (absValue < 0.01 && value !== 0) {
      // Very small value, show 6 digits after decimal
      return '$' + value.toFixed(6);
    } else if (absValue >= 1 || value <= -1) {
      // Large or negative, show 2 digits after decimal
      return '$' + value.toFixed(2);
    } else {
      // Otherwise, show 4 digits after decimal
      return '$' + value.toFixed(4);
    }
  };
  
  const allColumns = useMemo(() => [
    { id: 'label', label: t('tables.portfolio.label', 'Label'), align: 'left' },
    { id: 'blockchain', label: t('tables.portfolio.blockchain', 'Blockchain'), align: 'left' },
    { id: 'total_market_value', label: t('tables.portfolio.marketValue', 'Market Value'), align: 'right' },
    { id: 'address', label: t('tables.portfolio.address', 'Address'), align: 'left' }
  ], [t]);

  const sortedWallets = useMemo(() => {
    if (!wallets) return [];
    
    const sorted = [...wallets].sort((a, b) => {
      const aValue = a[sortColumn] || 0;
      const bValue = b[sortColumn] || 0;
      
      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return sorted;
  }, [wallets, sortColumn, sortDirection]);

  // Map wallet.blockchain to BlockchainProvider symbol
  const blockchainSymbolMap = {
    solana: 'SOL',
    bitcoin: 'BTC',
    ethereum: 'ETH',
    xrp: 'XRPL',
    xrpl: 'XRPL',
    stellar: 'stellar',
    cardano: 'cardano',
    litecoin: 'litecoin',
    hedera: 'hedera',
    avalanche: 'avalanche',
    xdc: 'xdc',
    dogecoin: 'dogecoin',
    kaspa: 'kaspa',
    sui: 'SUI'
  };

  // Map user selection to backend canonical blockchain value for wallet creation
  const backendBlockchainMap = {
    sol: 'solana',
    solana: 'solana',
    btc: 'bitcoin',
    bitcoin: 'bitcoin',
    eth: 'ethereum',
    ethereum: 'ethereum',
    xrpl: 'xrpl',
    xrp: 'xrpl'
  };

  // Pass prices to column renderers
  const columnRenderers = {
    label: (wallet) => <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{wallet.label || 'N/A'}</Typography>,
    blockchain: (wallet) => {
      const blockchain = wallet.blockchain || 'unknown';
      
      // First, try to find blockchain data directly (for exchanges like kucoin, kraken, etc.)
      let blockchainData = blockchains.find(b => b.symbol.toLowerCase() === blockchain.toLowerCase());
      
      // If not found directly, try using the blockchainSymbolMap (for regular blockchains like ethereum -> ETH)
      if (!blockchainData) {
        const mappedSymbol = blockchainSymbolMap[blockchain.toLowerCase()];
        if (mappedSymbol) {
          blockchainData = blockchains.find(b => b.symbol === mappedSymbol);
        }
      }
      
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {blockchainData?.icon_url && (
            <img 
              src={blockchainData.icon_url} 
              alt={blockchainData.name || blockchain} 
              style={{ width: 20, height: 20, borderRadius: '50%' }} 
            />
          )}
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {blockchainData?.name || blockchain}
          </Typography>
        </Box>
      );
    },
    address: (wallet) => (
      <Link 
        href={`${SUPPORTED_BLOCKCHAINS.find(b => b.id === wallet.blockchain)?.explorerUrl}${wallet.address}`}
        target="_blank" 
        rel="noopener noreferrer" 
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <OpenInNewIcon fontSize="small" sx={{ mr: 0.5 }} />
        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
          {wallet.address}
        </Typography>
      </Link>
    ),
    total_market_value: (wallet) => {
      const marketValue = wallet.total_market_value || 0;
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          backgroundColor: wallet._justUpdated ? 'success.light' : 'transparent',
          borderRadius: wallet._justUpdated ? 1 : 0,
          px: wallet._justUpdated ? 1 : 0,
          py: wallet._justUpdated ? 0.5 : 0,
          transition: 'all 0.3s ease'
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: marketValue < 0 ? 'error.main' : 'success.main', 
              fontWeight: 'bold',
              animation: wallet._justUpdated ? 'pulse 2s ease-in-out' : 'none'
            }}
          >
            {formatCurrency(marketValue)}
          </Typography>
          {wallet._justUpdated && (
            <Chip 
              label="Updated" 
              size="small" 
              color="success" 
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
        </Box>
      );
    },
  };

  useEffect(() => {
    const handleLanguageChange = () => {
      setIsRTL(i18n.dir() === 'rtl');
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const handleSubmit = async () => {
    if (!newWallet.blockchain || !newWallet.address) {
      setModalError('Blockchain and address are required.');
      return;
    }

    // Always send the backend canonical value for blockchain
    const blockchain = newWallet.blockchain || '';
    const blockchainToSend = backendBlockchainMap[blockchain.toLowerCase()] || blockchain.toLowerCase();

    // Close modal immediately and reset form
    handleClose(false);

    try {
      // Add wallet - this will trigger sync progress UI and refresh all data
      const result = await addWallet({
        ...newWallet,
        blockchain: blockchainToSend
      });
      
      // Refresh all data after wallet addition
      const [freshWallets, freshBalances, freshTransactions] = await Promise.all([
        getWallets(),
        refreshGroupedBalances(),
        fetchTransactions(transactionsPerPage, 0, { ...transactionFilters, ...transactionSort })
      ]);
      
      // Update state with fresh data
      setWallets(freshWallets || []);
      
      // Trigger price refresh after wallet add
      if (typeof refreshPrices === 'function') {
        refreshPrices();
      }
      
      // Refresh coins to get any new coins that might have been added during wallet sync
      await refreshCoins();
      
      // Success message will be shown in SyncProgress UI
    } catch (err) {
      let errorMsg = err.response?.data?.error || err.message || 'Failed to add wallet';
      if (err.response?.data?.details) {
        errorMsg += ': ' + err.response.data.details;
      }
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    }
  };

  const fetchWalletTransactions = async (walletId) => {
    // This function is deprecated - transactions are now handled by the wallet sync system
  };

  const handleRefreshWallet = async (walletId) => {
    try {
      // Find the wallet data to get address and blockchain
      const wallet = wallets.find(w => w.id === walletId);
      if (!wallet) {
        throw new Error('Wallet not found');
      }
      await syncWallet(walletId, currentUser.uid, wallet.address, wallet.blockchain);
      // Clear wallet cache and force a fresh fetch
      // Assuming clearWalletCache and getCachedWalletsData are defined elsewhere or will be added.
      // For now, we'll just reload the wallets directly.
      const freshWallets = await getWallets({ fresh: true });
      setWallets(freshWallets || []);
      // Optionally, refresh other data as well
      await Promise.all([
        refreshGroupedBalances(),
        fetchTransactions(transactionsPerPage, 0, { ...transactionFilters, ...transactionSort })
      ]);
    } catch (err) {
      let errorMsg = err.response?.data?.error || err.message || 'Failed to refresh wallet';
      if (err.response?.data?.details) {
        errorMsg += ': ' + err.response.data.details;
      }
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    }
  };

  const resetWalletForm = () => {
    setNewWallet({ blockchain: '', address: '', label: '' });
    setModalError('');
  };

  const resetTransactionForm = () => {
    setNewTransaction({
      date: new Date().toISOString().slice(0, 10),
      type: 'Buy',
      coin: '',
      blockchain: '',
      amount: '',
      price: '',
      fee: '0'
    });
    setModalError('');
  };

  const handleClickOpen = () => {
    setModalType('wallet');
    setOpen(true);
  };

  const handleOpenModal = (type) => {
    setModalType(type);
    if (type === 'transaction') {
      setNewTransaction({
        date: new Date().toISOString().slice(0, 10),
        type: 'Buy',
        coin: '',
        blockchain: '',
        amount: '',
        price: '',
        fee: '0'
      });
    } else {
      setNewWallet({
        blockchain: '',
        address: '',
        label: ''
      });
    }
    setOpen(true);
  };

  const handleClose = (fromCancel = true) => {
    setOpen(false);
    setModalError('');
    setEditingTransaction(null); // Clear editing state
    // Always reset both forms when modal is closed
    resetWalletForm();
    resetTransactionForm();
  };

  const handleDeleteClick = (id) => {
    setWalletToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setWalletToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!walletToDelete) return;
    try {
      await handleDeleteWallet(walletToDelete);
    } catch (err) {
      let errorMsg = err.response?.data?.error || err.message || 'Failed to delete wallet';
      if (err.response?.data?.details) {
        errorMsg += ': ' + err.response.data.details;
      }
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    }
  };

  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
      setSortColumn(column);
  };

  const tempHiddenColumns = React.useMemo(() => {
    return allColumns.filter(col => !tempVisibleColumns.includes(col.id));
  }, [allColumns, tempVisibleColumns]);

  const handleColumnMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setAnchorEl(null);
  };

  const handleToggleColumn = (colId) => {
    setVisibleColumns((prev) =>
      prev.includes(colId) ? prev.filter((c) => c !== colId) : [...prev, colId]
    );
  };
  
  useEffect(() => {
    setTempVisibleColumns(visibleColumns);
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem('walletVisibleColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    const savedColumns = localStorage.getItem('walletVisibleColumns');
    if (savedColumns) {
      setVisibleColumns(JSON.parse(savedColumns));
    }
  }, []);

  // Manual holdings localStorage persistence
  useEffect(() => {
    localStorage.setItem('manualHoldingsSortColumn', manualHoldingsSortColumn);
    localStorage.setItem('manualHoldingsSortDirection', manualHoldingsSortDirection);
    localStorage.setItem('manualHoldingsVisibleColumns', JSON.stringify(manualHoldingsVisibleColumns));
  }, [manualHoldingsSortColumn, manualHoldingsSortDirection, manualHoldingsVisibleColumns]);

  const handleMoveColumnUp = (index) => {
    if (index > 0) {
      const newCols = [...visibleColumns];
      [newCols[index - 1], newCols[index]] = [newCols[index], newCols[index - 1]];
      setVisibleColumns(newCols);
    }
  };
  
  const handleMoveColumnDown = (index) => {
    if (index < visibleColumns.length - 1) {
      const newCols = [...visibleColumns];
      [newCols[index + 1], newCols[index]] = [newCols[index], newCols[index + 1]];
      setVisibleColumns(newCols);
    }
  };
  
  const handleHideColumn = (colId) => {
    if (visibleColumns.length > 1) {
      setVisibleColumns(visibleColumns.filter((c) => c !== colId));
    }
  };
  
  const handleShowColumn = (colId) => {
    const col = allColumns.find(c => c.id === colId);
    if (col) {
      setVisibleColumns([...visibleColumns, colId]);
    }
  };

  const handleOpenColumnsDialog = () => {
    setAnchorEl(null);
    setColumnsDialogOpen(true);
    setTempVisibleColumns(visibleColumns);
  };

  const handleCloseColumnsDialog = () => {
    setColumnsDialogOpen(false);
  };

  const handleSaveColumnsDialog = () => {
    setVisibleColumns(tempVisibleColumns);
    setColumnsDialogOpen(false);
  };

  // Fetch unique coins for the dropdown
  useEffect(() => {
    async function fetchUniqueCoins() {
      if (!transactionFilters.walletId) {
        // All Wallets: fetch from API
        try {
          const res = await api.get('/api/wallets/unique-coins');
          setUniqueCoins(res.data.coins || []);
        } catch (err) {
          setUniqueCoins([]);
        }
      } else {
        // Specific wallet: use coins from current transactions
        const coins = [
          ...new Set(
            transactions
              .filter(tx => String(tx.wallet_id) === String(transactionFilters.walletId))
              .map(tx => tx.coin)
              .filter(Boolean)
          ),
        ].sort();
        setUniqueCoins(coins);
      }
    }
    fetchUniqueCoins();
  }, [transactionFilters.walletId, transactions]);

  // Manual holdings functions
  const fetchManualHoldings = async () => {
    if (!currentUser) return;
    setManualHoldingsLoading(true);
    try {
      const response = await api.get('/api/transactions', {
        params: { user_id: currentUser.uid }
      });
      setManualHoldings(response.data || []);
    } catch (error) {
      console.error('Error fetching manual holdings:', error);
      setError('Failed to fetch manual holdings');
    } finally {
      setManualHoldingsLoading(false);
    }
  };

  // Fetch portfolio gains
  const fetchPortfolioGains = async () => {
    if (!currentUser) return;
    setLoadingGains(true);
    
    const maxRetries = 3;
    let retryCount = 0;
    
    while (retryCount < maxRetries) {
      try {
        // console.log('[PortfolioGains] Fetching portfolio gains from API...');
        const response = await api.get('/api/wallets/portfolio-gains');
        const gainsMap = {};
        if (response.data && response.data.gains) {
          response.data.gains.forEach(gain => {
            gainsMap[gain.coin] = gain;
            // Only log BTC for debugging
            if (gain.coin === 'BTC') {
              // console.log(`[PortfolioGains] BTC processed:`, {
              //   gain_24h: gain.gain_24h,
              //   gain_1m: gain.gain_1m,
              //   gain_all_time: gain.gain_all_time
              // });
            }
          });
        }
        // console.log('[PortfolioGains] Setting portfolio gains state:', gainsMap);
        setPortfolioGains(gainsMap);
        return; // Success, exit the retry loop
      } catch (error) {
        retryCount++;
        console.error(`Error fetching portfolio gains (attempt ${retryCount}/${maxRetries}):`, error);
        
        if (retryCount >= maxRetries) {
          // Final attempt failed, try to calculate basic gains from current data
          // console.log('Backend portfolio gains failed, calculating basic gains from current data');
          await calculateBasicGains();
        } else {
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      }
    }
    
    setLoadingGains(false);
  };

  // Fallback function to calculate basic gains from current data
  const calculateBasicGains = async () => {
    try {
      const gainsMap = {};
      
      // Group transactions by coin
      const coinGroups = {};
      manualHoldings.forEach(tx => {
        if (!coinGroups[tx.coin]) {
          coinGroups[tx.coin] = [];
        }
        coinGroups[tx.coin].push(tx);
      });

      // Calculate basic gains for each coin
      for (const [coin, transactions] of Object.entries(coinGroups)) {
        const currentPrice = prices[coin]?.usd || 0;
        let totalAmount = 0;
        let totalCost = 0;

        transactions.forEach(tx => {
          const amount = parseFloat(tx.amount || tx.shares || 0);
          const price = parseFloat(tx.buy_price || tx.price || tx.costPerShare || 0);
          const fee = parseFloat(tx.fee || 0);

          if (tx.type === 'Buy') {
            totalAmount += amount;
            totalCost += (amount * price) + fee;
          } else {
            totalAmount -= amount;
            totalCost -= (amount * price) + fee;
          }
        });

        if (totalAmount > 0 && currentPrice > 0) {
          const currentValue = totalAmount * currentPrice;
          const avgCost = totalCost / totalAmount;
          const totalGain = currentValue - totalCost;
          const totalGainPercent = avgCost > 0 ? ((currentPrice - avgCost) / avgCost) * 100 : 0;

          gainsMap[coin] = {
            coin,
            total_amount: totalAmount,
            current_value: currentValue,
            gain_24h: 0, // Can't calculate without historical data
            gain_24h_percent: 0,
            gain_1m: 0, // Can't calculate without historical data
            gain_1m_percent: 0,
            gain_all_time: totalGain,
            gain_all_time_percent: totalGainPercent
          };
        }
      }

      setPortfolioGains(gainsMap);
    } catch (error) {
      console.error('Error calculating basic gains:', error);
      setPortfolioGains({});
    }
  };

  const handleAddManualHolding = async (transactionData) => {
    try {
      // console.log(`[ManualHoldings] Adding manual holding:`, transactionData);
      
      const response = await api.post('/api/transactions', transactionData);
      
      if (response.data) {
        // console.log(`[ManualHoldings] Manual holding added successfully:`, response.data);
        
        // Refresh prices with icons since a new coin might have been added
        // console.log(`[ManualHoldings] Triggering price refresh with icons after adding manual holding`);
        refreshWithIcons();
        
        // Refresh manual holdings
        await fetchManualHoldings();
        
        // Add a small delay to ensure database transaction is committed
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Refresh all data to update all tabs
        await Promise.all([
          // Refresh wallets tab - use fresh flag to ensure we get the latest data
          getWallets({ fresh: true }).then(freshWallets => {
            setWallets(freshWallets || []);
          }),
          // Refresh wallet transactions tab
          fetchTransactions(transactionsPerPage, 0, { ...transactionFilters, ...transactionSort }),
          // Refresh portfolio overview tab
          refreshGroupedBalances()
        ]);
        
        // Close modal
        handleClose(false);
        
        // Show success message
        setSnackbar({ 
          open: true, 
          message: 'Manual holding added successfully', 
          severity: 'success' 
        });
      }
    } catch (err) {
      console.error(`[ManualHoldings] Error adding manual holding:`, err);
      let errorMsg = err.response?.data?.error || err.message || 'Failed to add manual holding';
      if (err.response?.data?.details) {
        errorMsg += ': ' + err.response.data.details;
      }
      setModalError(errorMsg);
    }
  };

  const handleEditManualHolding = async (transactionId, updatedData) => {
    try {
      const response = await api.put(`/api/transactions/${transactionId}`, updatedData);
      
      // Update manual holdings list
      setManualHoldings(prev => 
        prev.map(holding => 
          holding.id === transactionId ? response.data : holding
        )
      );
      
      // Add a small delay to ensure database transaction is committed
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh all data to update all tabs
      await Promise.all([
        // Refresh wallets tab - use fresh flag to ensure we get the latest data
        getWallets({ fresh: true }).then(freshWallets => {
          setWallets(freshWallets || []);
        }),
        // Refresh wallet transactions tab
        fetchTransactions(transactionsPerPage, 0, { ...transactionFilters, ...transactionSort }),
        // Refresh portfolio overview tab
        refreshGroupedBalances()
      ]);
      
      setSnackbar({
        open: true,
        message: 'Manual holding updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating manual holding:', error);
      let errorMsg = error.response?.data?.error || 'Failed to update manual holding';
      if (error.response?.data?.details) {
        errorMsg += ': ' + error.response.data.details;
      }
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
    }
  };

  const handleDeleteManualHolding = async (transactionId) => {
    try {
      await api.delete(`/api/transactions/${transactionId}`);
      
      // Update manual holdings list
      setManualHoldings(prev => prev.filter(holding => holding.id !== transactionId));
      
      // Add a small delay to ensure database transaction is committed
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reset wallet filter to "All Wallets" to avoid filtering by deleted wallet
      const updatedFilters = { ...transactionFilters, walletId: '' };
      setTransactionFilters(updatedFilters);
      
      // Refresh all data to update all tabs
      await Promise.all([
        // Refresh wallets tab - use fresh flag to ensure we get the latest data
        getWallets({ fresh: true }).then(freshWallets => {
          setWallets(freshWallets || []);
        }),
        // Refresh wallet transactions tab with reset filters
        fetchTransactions(transactionsPerPage, 0, { ...updatedFilters, ...transactionSort }),
        // Refresh portfolio overview tab
        refreshGroupedBalances()
      ]);
      
      setSnackbar({
        open: true,
        message: 'Manual holding deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting manual holding:', error);
      let errorMsg = error.response?.data?.error || 'Failed to delete manual holding';
      if (error.response?.data?.details) {
        errorMsg += ': ' + error.response.data.details;
      }
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
    }
  };

  // Manual holdings table functions
  const handleManualHoldingsSort = (column) => {
    const isAsc = manualHoldingsSortColumn === column && manualHoldingsSortDirection === 'asc';
    setManualHoldingsSortDirection(isAsc ? 'desc' : 'asc');
    setManualHoldingsSortColumn(column);
  };

  const handleManualHoldingsSearch = (searchTerm) => {
    setManualHoldingsSearchTerm(searchTerm);
  };

  const handleManualHoldingsDeleteClick = (id) => {
    setManualHoldingsToDelete(id);
    setManualHoldingsDeleteConfirmOpen(true);
  };

  const handleManualHoldingsDeleteCancel = () => {
    setManualHoldingsDeleteConfirmOpen(false);
    setManualHoldingsToDelete(null);
  };

  const handleManualHoldingsDeleteConfirm = async () => {
    try {
      await handleDeleteManualHolding(manualHoldingsToDelete);
    } finally {
      setManualHoldingsDeleteConfirmOpen(false);
      setManualHoldingsToDelete(null);
    }
  };

  // Manual holdings column management
  const handleManualHoldingsOpenColumnsDialog = () => {
    setManualHoldingsTempVisibleColumns([...manualHoldingsVisibleColumns]);
    setManualHoldingsTempHiddenColumns(manualHoldingsAllColumns.filter(col => !manualHoldingsVisibleColumns.includes(col.id)).map(col => col.id));
    setManualHoldingsColumnsDialogOpen(true);
  };

  const handleManualHoldingsCloseColumnsDialog = () => {
    setManualHoldingsColumnsDialogOpen(false);
  };

  const handleManualHoldingsSaveColumnsDialog = () => {
    setManualHoldingsVisibleColumns([...manualHoldingsTempVisibleColumns]);
    setManualHoldingsColumnsDialogOpen(false);
  };

  const handleManualHoldingsMoveColumnUp = (index) => {
    if (index === 0) return;
    setManualHoldingsTempVisibleColumns((prev) => {
      const newOrder = [...prev];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      return newOrder;
    });
  };

  const handleManualHoldingsMoveColumnDown = (index) => {
    if (index === manualHoldingsTempVisibleColumns.length - 1) return;
    setManualHoldingsTempVisibleColumns((prev) => {
      const newOrder = [...prev];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      return newOrder;
    });
  };

  const handleManualHoldingsHideColumn = (colId) => {
    if (manualHoldingsTempVisibleColumns.length === 1) return;
    setManualHoldingsTempVisibleColumns(manualHoldingsTempVisibleColumns.filter(id => id !== colId));
    setManualHoldingsTempHiddenColumns([...manualHoldingsTempHiddenColumns, colId]);
  };

  const handleManualHoldingsShowColumn = (colId) => {
    setManualHoldingsTempHiddenColumns(manualHoldingsTempHiddenColumns.filter(id => id !== colId));
    setManualHoldingsTempVisibleColumns([...manualHoldingsTempVisibleColumns, colId]);
  };

  // Reset manual holdings columns to show all columns
  const resetManualHoldingsColumns = () => {
    const allColumnIds = manualHoldingsAllColumns.map(col => col.id);
    setManualHoldingsVisibleColumns(allColumnIds);
    localStorage.removeItem('manualHoldingsVisibleColumns');
  };

  // Manual holdings data processing
  const filteredManualHoldings = useMemo(() => {
    if (!manualHoldings || manualHoldings.length === 0) return [];
    
    let filtered = manualHoldings;
    
    // Apply search filter
    if (manualHoldingsSearchTerm) {
      const searchLower = manualHoldingsSearchTerm.toLowerCase();
      filtered = filtered.filter(tx => 
        tx.coin?.toLowerCase().includes(searchLower) ||
        tx.type?.toLowerCase().includes(searchLower) ||
        tx.blockchain?.toLowerCase().includes(searchLower) ||
        tx.date?.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [manualHoldings, manualHoldingsSearchTerm]);

  const sortedManualHoldings = useMemo(() => {
    return [...filteredManualHoldings].sort((a, b) => {
      let aValue, bValue;
      switch (manualHoldingsSortColumn) {
        case 'date':
          aValue = a.date;
          bValue = b.date;
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        case 'coin':
          aValue = a.coin;
          bValue = b.coin;
          break;
        case 'coinName':
          aValue = coins.find(c => c.symbol === a.coin)?.name || '';
          bValue = coins.find(c => c.symbol === b.coin)?.name || '';
          break;
        case 'blockchain':
          aValue = a.blockchain || '';
          bValue = b.blockchain || '';
          break;
        case 'amount':
          aValue = Number(a.amount || a.shares || 0);
          bValue = Number(b.amount || b.shares || 0);
          break;
        case 'price':
          aValue = Number(a.buy_price || a.price || a.costPerShare || 0);
          bValue = Number(b.buy_price || b.price || b.costPerShare || 0);
          break;
        case 'fee':
          aValue = Number(a.fee || 0);
          bValue = Number(b.fee || 0);
          break;
        case 'total':
          aValue = (Number(a.amount || a.shares || 0) * Number(a.buy_price || a.price || a.costPerShare || 0)) + Number(a.fee || 0);
          bValue = (Number(b.amount || b.shares || 0) * Number(b.buy_price || b.price || b.costPerShare || 0)) + Number(b.fee || 0);
          break;
        default:
          aValue = a.date;
          bValue = b.date;
      }
      
      if (typeof aValue === 'string') {
        return manualHoldingsSortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return manualHoldingsSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });
  }, [filteredManualHoldings, manualHoldingsSortColumn, manualHoldingsSortDirection, coins]);

  // Manual holdings column renderers
  const manualHoldingsColumnRenderers = {
    date: (tx) => new Date(tx.date).toLocaleDateString(),
    type: (tx) => tx.type,
    coin: (tx) => tx.coin,
    coinName: (tx) => {
      const coinObj = coins.find(c => c.symbol === tx.coin);
      return coinObj ? coinObj.name : '-';
    },
    blockchain: (tx) => tx.blockchain || 'Unknown',
    amount: (tx) => parseFloat(tx.amount || tx.shares || 0).toFixed(6),
    price: (tx) => {
      const historicalPrice = parseFloat((tx.buy_price ?? tx.price ?? tx.costPerShare) || 0);
      const currentPrice = prices[tx.coin]?.usd ?? 0;
      return {
        historical: formatCurrency(historicalPrice),
        current: formatCurrency(currentPrice)
      };
    },
    fee: (tx) => formatCurrency(parseFloat(tx.fee || 0)),
    total: (tx) => {
      const historicalTotal = (parseFloat(tx.amount || tx.shares || 0) * parseFloat((tx.buy_price ?? tx.price ?? tx.costPerShare) || 0)) + parseFloat(tx.fee || 0);
      const currentTotal = (parseFloat(tx.amount || tx.shares || 0) * (prices[tx.coin]?.usd ?? 0)) + parseFloat(tx.fee || 0);
      return {
        historical: formatCurrency(historicalTotal),
        current: formatCurrency(currentTotal)
      };
    },
  };

  // Add a function to format the last update time
  const getLastUpdateText = () => {
    if (isRefreshing) return t('portfolio.updatingPrices', 'Updating prices...');
    if (!lastUpdate) return t('portfolio.loadingPrices', 'Loading prices...');
    
    const now = new Date();
    const diff = now - new Date(lastUpdate);
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return t('portfolio.lastUpdatedDaysAgo', { count: days }, `Last updated ${days} days ago`);
    if (hours > 0) return t('portfolio.lastUpdatedHoursAgo', { count: hours }, `Last updated ${hours} hours ago`);
    if (minutes > 0) return t('portfolio.lastUpdatedMinutesAgo', { count: minutes }, `Last updated ${minutes} minutes ago`);
    return t('portfolio.lastUpdatedJustNow', 'Last updated just now');
  };

  // Get freshness display info
  const getFreshnessDisplay = () => {
    if (!freshnessInfo) return { text: 'No data', color: 'text-gray-500' };
    
    const { fresh, stale, oldestAge, summary, batchesProcessed } = freshnessInfo;
    const total = fresh + stale;
    
    if (total === 0) return { text: 'No data', color: 'text-gray-500' };
    
    // Calculate freshness percentage
    const freshnessPercentage = (fresh / total) * 100;
    
    let color = 'text-red-500'; // Default to red for stale
    if (freshnessPercentage >= 80) {
      color = 'text-green-500'; // Green for mostly fresh
    } else if (freshnessPercentage >= 50) {
      color = 'text-yellow-500'; // Yellow for mixed
    }
    
    // Add batch indicator if multiple batches were processed
    let text = summary;
    if (batchesProcessed > 1) {
      text = `${summary} (${batchesProcessed} batches)`;
    }
    
    return { text, color };
  };

  return {
    // State
    activeTab,
    wallets,
    open,
    modalType,
    newWallet,
    newTransaction,
    editingTransaction,
    manualHoldings,
    manualHoldingsLoading,
    loading,
    error,
    modalError,
    currentUser,
    walletTransactions,
    loadingTransactions,
    deleteConfirmOpen,
    walletToDelete,
    transactionFilters,
    transactionSort,
    sortColumn,
    sortDirection,
    visibleColumns,
    anchorEl,
    columnsDialogOpen,
    tempVisibleColumns,
    isRTL,
    snackbar,
    realOnChainBalance,
    missingAmount,
    uniqueCoins,
    blockchains,
    blockchainsLoading,
    coins,
    coinsLoading,
    refreshCoins,
    syncLoading,
    syncError,
    groupedBalances,
    transactions,
    syncProgress,
    transactionPagination,
    prices,
    loadingPrices,
    pricesError,
    refreshPrices,
    refreshWithIcons,
    forceRefreshPrices,
    lastUpdate,
    freshnessInfo,
    isRefreshing,
    allCoinIdentifiers,
    allTransactionCoins,
    allColumns,
    sortedWallets,
    blockchainSymbolMap,
    backendBlockchainMap,
    columnRenderers,
    tempHiddenColumns,
    manualHoldingsSearchTerm,
    manualHoldingsSortColumn,
    manualHoldingsSortDirection,
    manualHoldingsDeleteConfirmOpen,
    manualHoldingsToDelete,
    manualHoldingsAllColumns,
    manualHoldingsVisibleColumns,
    manualHoldingsColumnsDialogOpen,
    manualHoldingsTempVisibleColumns,
    manualHoldingsTempHiddenColumns,
    filteredManualHoldings,
    sortedManualHoldings,
    manualHoldingsColumnRenderers,
    portfolioGains,
    loadingGains,

    // Actions
    setActiveTab,
    setWallets,
    setOpen,
    setModalType,
    setNewWallet,
    setNewTransaction,
    setEditingTransaction,
    setManualHoldings,
    setManualHoldingsLoading,
    setManualHoldingsSearchTerm,
    setManualHoldingsSortColumn,
    setManualHoldingsSortDirection,
    setManualHoldingsDeleteConfirmOpen,
    setManualHoldingsToDelete,
    setManualHoldingsVisibleColumns,
    setManualHoldingsColumnsDialogOpen,
    setManualHoldingsTempVisibleColumns,
    setManualHoldingsTempHiddenColumns,
    setLoading,
    setError,
    setModalError,
    setWalletTransactions,
    setLoadingTransactions,
    setDeleteConfirmOpen,
    setWalletToDelete,
    setTransactionFilters,
    setTransactionSort,
    setSortColumn,
    setSortDirection,
    setVisibleColumns,
    setAnchorEl,
    setColumnsDialogOpen,
    setTempVisibleColumns,
    setIsRTL,
    setSnackbar,
    setRealOnChainBalance,
    setMissingAmount,
    setUniqueCoins,
    setPortfolioGains,
    setLoadingGains,

    // Handlers
    handleTabChange,
    handleTransactionsPageChange,
    handleFilterChange,
    handleTransactionSort,
    handleSyncWallet,
    handleDeleteWallet,
    handleManualRefresh,
    handleSubmit,
    fetchWalletTransactions,
    handleRefreshWallet,
    resetWalletForm,
    resetTransactionForm,
    handleClickOpen,
    handleOpenModal,
    handleClose,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleSort,
    handleColumnMenuOpen,
    handleColumnMenuClose,
    handleToggleColumn,
    handleMoveColumnUp,
    handleMoveColumnDown,
    handleHideColumn,
    handleShowColumn,
    handleOpenColumnsDialog,
    handleCloseColumnsDialog,
    handleSaveColumnsDialog,
    fetchManualHoldings,
    fetchPortfolioGains,
    calculateBasicGains,
    handleAddManualHolding,
    handleEditManualHolding,
    handleDeleteManualHolding,
    handleManualHoldingsSort,
    handleManualHoldingsSearch,
    handleManualHoldingsDeleteClick,
    handleManualHoldingsDeleteCancel,
    handleManualHoldingsDeleteConfirm,
    handleManualHoldingsOpenColumnsDialog,
    handleManualHoldingsCloseColumnsDialog,
    handleManualHoldingsSaveColumnsDialog,
    handleManualHoldingsMoveColumnUp,
    handleManualHoldingsMoveColumnDown,
    handleManualHoldingsHideColumn,
    handleManualHoldingsShowColumn,
    resetManualHoldingsColumns,
    getLastUpdateText,
    getFreshnessDisplay,
    formatCurrency,
    loadInitialData,
    syncWallet,
    addWallet,
    refreshGroupedBalances,
    fetchTransactions,
    getWallets,
    deleteWallet,
    clearError,
    clearSyncProgress,
    clearGroupedBalances,
    setTransactions
  };
}; 